import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import useReportStore from '../../store/report';
import { createColumns } from './columns';

export const PartiesTable: FC = () => {
  const { t } = useTranslation();
  const columns = createColumns();
  const { reportParties, reportPartiesIsLoading } = useReportStore();

  const table = useReactTable({
    data: reportParties?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="bg-white rounded-md h-[50%] overflow-auto border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="text-xs font-medium text-gray-800 border-b border-gray-200"
            >
              <TableHead className="w-[50px]">№</TableHead>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="whitespace-wrap w-50 p-2">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}

          <TableRow className="bg-gray-100 font-semibold text-gray-900 border-b">
            <TableHead className="text-left">{t('total')}</TableHead>
            {columns.map((column) => {
              const columnDef = column as { accessorKey?: string };
              const accessorKey = columnDef.accessorKey || '';
              const totalValue = reportParties?.total?.[accessorKey];

              return (
                <TableHead
                  key={accessorKey || column.id}
                  className="whitespace-wrap w-50 p-2"
                >
                  {totalValue !== undefined && totalValue !== null
                    ? totalValue
                    : ''}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>

        <TableBody>
          {reportPartiesIsLoading ? (
            [...Array(10)].map((_, index) => (
              <TableRow key={index}>
                <TableCell className="h-[52px] text-center">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-8 mx-auto" />
                </TableCell>
                {columns.map((_, cellIndex) => (
                  <TableCell key={cellIndex} className="h-[52px]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                className="hover:bg-light border-b border-gray-200 text-gray-600"
              >
                <TableCell className="text-center text-black font-medium">
                  {index + 1}
                </TableCell>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="h-[52px]">
                    <div className="min-w-28">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                className="h-[52px] text-center"
              >
                {t('no.data')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
